import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ModalsContext } from '../../contexts/ModalsContext';
import { AlertContext } from '../../contexts/AlertContext';
import { SocketContext } from '../../contexts/SocketContext';
import { LoaderContext } from '../../contexts/LoaderContext';
import { CreateQuestionForm } from '../../components/surveys/questions/CreateQuestionForm';
import { convertirLetraMayus } from '../../helpers/convertirLetraMayus';
import { Actions } from '../../components/surveys/questions/Actions';
import { useModalStore } from '../../store/useModalStore';

const columns = [
    { id: "pregunta", label: "Pregunta", minWidth: 100, align: 'center' },
    { id: "codigo", label: "Código", minWidth: 50, align: 'center' },
    { id: "tipo_respuesta", label: "Tipo respuesta", minWidth: 170, align: 'center' },
    { id: "estado", label: "Estado", minWidth: 170, align: 'center' },
    { id: "acciones", label: "Acciones", minWidth: 170, align: 'center' }
];

export const useQuestions = () => {

    const [questions, setQuestions] = useState([]);

    // const { modalRef } = useContext(ModalsContext);
    const { alertRef } = useContext(AlertContext);
    const { socket } = useContext(SocketContext);
    const { setOpenLoader } = useContext(LoaderContext);
    const { openModal, closeModal } = useModalStore();

    useEffect(() => {
        let subscribed = true;
        
        if(subscribed) 
            setOpenLoader(true);
            getQuestions();

        return () => subscribed = false;
    }, []);

    const searchQuestion = (value) => { 
        socket.emit('frontend:buscar-pregunta', { codigo: value?.toLowerCase()?.trim() }, response => {
            const { ok, message, data } = response;

            if(!ok) {
                alertRef.current?.handleOpen('error', message);
                return;
            }
          
            setQuestions(data?.map(d => generatePayload(d)));
        });
    }

    // const addQuestion = () => {
    //     modalRef.current.handleOpen(<CreateQuestionForm action={ (data) => saveQuestion(data)  } />, 'auto', 'auto');
    // }

    const addQuestion = () => openModal(<CreateQuestionForm action={ (data) => saveQuestion(data)  } />);

    const getQuestions = useCallback(() => {
        socket.emit('frontend:obtener-preguntas', (response) => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                alertRef.current?.handleOpen('error', message);
                return;
            }
           
            const newArr = data?.map(generatePayload);
            setQuestions(newArr);
            setOpenLoader(false);
        });
    }, []);

    const saveQuestion = (args) => {
        setOpenLoader(true);
        const{ pregunta, instrucciones, codigo, opcion_respuestas, nombre, ...rest } = args;
        socket.emit('frontend:crear-pregunta', { 
            pregunta: pregunta?.trim(),
            instrucciones: instrucciones?.trim(),
            codigo: codigo?.toLowerCase()?.trim(),
            tipo_respuesta: {
                nombre: nombre.toLowerCase().trim(),
                opciones: Object.values(rest)
            },
        }, response => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                // modalRef.current?.handleClose();
                closeModal();
                alertRef.current?.handleOpen('error', message);
                return;
            }

            setQuestions(prev => ([...prev, generatePayload(data)]));
            // modalRef.current?.handleClose();
            closeModal();
            setOpenLoader(false);
        });
    }

    const updateQuestion = (args) => {
        setOpenLoader(true);
        const { _id, pregunta, instrucciones, codigo, opcion_respuestas, nombre, activo, ...rest } = args;
        socket.emit('frontend:actualizar-pregunta', { 
            _id, 
            pregunta: pregunta?.trim(),
            instrucciones: instrucciones?.trim(),
            tipo_respuesta: {
                nombre: nombre.toLowerCase().trim(),
                opciones: Object.values(rest)
            },
            activo: Boolean(activo)
        }, response => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                // modalRef.current?.handleClose();
                closeModal();
                alertRef.current?.handleOpen('error', message);
                return;
            }
            
            setQuestions(prev => prev?.map(p => p?._id === _id ? generatePayload(data) : p ));
            // modalRef.current?.handleClose();
            closeModal();
            setOpenLoader(false);
        });
    }

    const duplicateQuestion = (args) => {
        setOpenLoader(true);
        const{ nombre, activo, pregunta, instrucciones, codigo, opcion_respuestas, ...rest } = args;
        socket.emit('frontend:duplicar-pregunta', { 
            pregunta: pregunta?.trim(),
            instrucciones: instrucciones?.trim(),
            codigo: codigo.toLowerCase().trim(),
            tipo_respuesta: {
                nombre: nombre.toLowerCase().trim(),
                opciones: Object.values(rest)
            },
            activo: Boolean(activo),
        }, response => {
            const { ok, message, data } = response;

            if(!ok) {
                setOpenLoader(false);
                // modalRef.current?.handleClose();
                closeModal();
                alertRef.current?.handleOpen('error', message);
                return;
            }

            setQuestions(prev => ([...prev, generatePayload(data)]));
            // modalRef.current?.handleClose();
            closeModal();
            setOpenLoader(false);
        });
    }

    const deleteQuestion = (_id) => {
        setOpenLoader(true);
        socket.emit('frontend:eliminar-pregunta', { _id }, response => {
            const { ok, message } = response;

            if(!ok) {
                setOpenLoader(false);
                // modalRef.current?.handleClose();
                closeModal();
                alertRef.current?.handleOpen('error', message);
                return;
            }

            setQuestions(prev => prev?.filter(p => p._id !== _id));
            // modalRef.current?.handleClose();
            closeModal();
            setOpenLoader(false);
        });
    }

    const generatePayload = (data) => ({
        ...data,
        pregunta: convertirLetraMayus(data?.pregunta),
        codigo: convertirLetraMayus(data?.codigo),
        tipo_respuesta: convertirLetraMayus(data?.tipo_respuesta?.nombre),
        estado: data?.activo ? 'Activo' : 'Inactivo',
        acciones: <Actions data={ data } updated={ updateQuestion } duplicated={ duplicateQuestion } deleted={ deleteQuestion } />
    });

    return {
        columns,
        questions,
        addQuestion,
        searchQuestion
    }
}